const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8050';
const CACHE_TTL_MS = 60_000;
const cache = new Map();

const request = async (path, options = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/curriculum${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.detail || 'Curriculum request failed');
  }
  return payload;
};

const cachedRequest = (path, ttl = CACHE_TTL_MS) => {
  const now = Date.now();
  const hit = cache.get(path);
  if (hit && now - hit.createdAt < ttl) return hit.promise;
  const promise = request(path).catch((err) => {
    cache.delete(path);
    throw err;
  });
  cache.set(path, { createdAt: now, promise });
  return promise;
};

const invalidateCache = (predicate) => {
  Array.from(cache.keys()).forEach((key) => {
    if (predicate(key)) cache.delete(key);
  });
};

export const fetchStats = () => cachedRequest('/stats', 30_000);

export const fetchTracks = () => cachedRequest('/tracks');

export const fetchReviewQueue = (limit = 20) => cachedRequest(`/review-queue?limit=${limit}`, 30_000);

export const fetchWorkspaceTemplates = () => request('/workspace-templates');

export const fetchTrack = (trackSlug) => cachedRequest(`/tracks/${trackSlug}`);

export const fetchTrackProgress = (trackSlug) => request(`/tracks/${trackSlug}/progress`);

export const fetchLearnerRepository = (learningPathKey) =>
  request(`/learning-paths/${learningPathKey}/repository`);

export const saveLearnerRepository = (learningPathKey, data) =>
  request(`/learning-paths/${learningPathKey}/repository`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const fetchCourse = (courseSlug) => request(`/courses/${courseSlug}`);

export const fetchLesson = (lessonSlug) => cachedRequest(`/lessons/${lessonSlug}`);

export const fetchLessonProgress = (lessonSlug) => cachedRequest(`/lessons/${lessonSlug}/progress`, 15_000);

export const saveLessonProgress = async (lessonSlug, data) => {
  const payload = await request(`/lessons/${lessonSlug}/progress`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  invalidateCache((key) =>
    key === '/stats' ||
    key === '/tracks' ||
    key === `/lessons/${lessonSlug}/progress` ||
    key.startsWith('/review-queue') ||
    key.startsWith('/tracks/')
  );
  return payload;
};

export const fetchLessonCommit = (lessonSlug) => request(`/lessons/${lessonSlug}/commit`);

export const saveLessonCommit = (lessonSlug, data) =>
  request(`/lessons/${lessonSlug}/commit`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
